'use client';

import { useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../../lib/utils';
import { Label } from './label';

/** The shadcn/ui Field parts: a labelled control with its description and error, and the groups that
 *  stack them. Plain elements in shadcn too; the label is the Radix Label primitive.
 *
 *  The anatomy and the three orientations are shadcn's. `responsive` is the settings record: the label
 *  and its description over the control on a phone, the control beside them once the surrounding
 *  `FieldGroup` is at least `@md` wide. It is a CONTAINER query, so a card squeezed by the advisor dock
 *  stacks exactly like a phone does, whatever the window is.
 *
 *  One deliberate addition: in a responsive field the control takes ONE width once it moves beside the
 *  label — `w-72`, stated here and nowhere else — so every picker, input and slider down a card ends on
 *  the same column instead of each sizing to its own content. That is the job `responsive` is for: a
 *  value control. A compact control that sits beside its label at every width (a switch, a button) uses
 *  `horizontal`, which keeps the control's own size. */

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn('flex min-w-0 flex-col gap-6', 'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3', className)}
      {...props}
    />
  );
}

function FieldLegend({ className, variant = 'legend', ...props }: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn('mb-3 tracking-ui', 'data-[variant=legend]:text-section-title tracking-ui', 'data-[variant=label]:text-card-title tracking-ui', className)}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full min-w-0 flex-col gap-6 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
        className,
      )}
      {...props}
    />
  );
}

const fieldVariants = cva('group/field flex w-full min-w-0 gap-3 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
      horizontal: [
        'flex-row items-center',
        '[&>[data-slot=field-label]]:flex-auto',
        'has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio],[role=switch]]:mt-px',
      ],
      responsive: [
        'flex-col [&>*]:w-full [&>.sr-only]:w-auto',
        '@md/field-group:flex-row @md/field-group:items-center @md/field-group:gap-6 @md/field-group:[&>*]:w-auto',
        '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
        '@md/field-group:[&>:not([data-slot=field-content],[data-slot=field-label],.sr-only)]:w-72 @md/field-group:[&>:not([data-slot=field-content],[data-slot=field-label],.sr-only)]:shrink-0',
        '@md/field-group:has-[>[data-slot=field-content]]:items-start',
      ],
    },
  },
  defaultVariants: { orientation: 'vertical' },
});

function Field({ className, orientation = 'vertical', ...props }: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="field-content" className={cn('group/field-content flex min-w-0 flex-1 flex-col gap-1.5', className)} {...props} />;
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        'group/field-label peer/field-label flex w-fit gap-2 group-data-[disabled=true]/field:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

/** A field's name when the control is not a labelable element — a picker trigger, a slider or a switch
 *  that already carries its own accessible name. Same look as `FieldLabel`, without the `<label>`. */
function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label"
      className={cn('flex w-fit max-w-full flex-wrap items-center gap-2 text-card-title tracking-ui group-data-[disabled=true]/field:opacity-50', className)}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'max-w-[var(--measure-prose)] text-meta tracking-ui text-muted-foreground',
        'last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5',
        'inline-links',
        className,
      )}
      {...props}
    />
  );
}

function FieldError({ className, children, errors, ...props }: React.ComponentProps<'div'> & { errors?: Array<{ message?: string } | undefined> }) {
  const content = useMemo(() => {
    if (children) return children;
    if (!errors?.length) return null;
    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];
    if (uniqueErrors.length === 1) return uniqueErrors[0]?.message;
    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) return null;
  return (
    <div role="alert" data-slot="field-error" className={cn('text-meta tracking-ui text-destructive', className)} {...props}>
      {content}
    </div>
  );
}

export { Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet, FieldTitle };
